import { zodResolver } from '@hookform/resolvers/zod'
import { Box, Button, Stack } from '@mui/material'
import { observer } from 'mobx-react'
import React from 'react'
import { useForm, UseFormWatch } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'
import { useInvalidateQueries } from '../../../../../hooks/useInvalidateQueries'
import { useTerritoryFields } from '../../../../../hooks/useTerritoryFields'
import { isAdminLabelAA } from '../../../../../services/AccountService'
import { updateRequest } from '../../../../../services/ApiService'
import { notifySuccess } from '../../../../../services/ToastifyService/ToastifyService'
import {
  closeModalFn,
  openModalFn,
} from '../../../../../utils/controllers/ModalController'
import { dataEditReportStatuses } from '../../../consts/request-body-data'
import singleRequestStore from '../../../store/SingleRequestStore'
import { RequestType } from '../../../types/Request.type'
import ChangeInfoForm from '../elements/ChangeInfoForm'
import { getDefaultInfoValues } from '../helpers/getDefaultValues'
import { ChangeInfoFormType, changeInfoSchema } from '../helpers/getFormSchema'

type DrawerContentRequestInfoProps = {
  request: RequestType
  onClose: () => void
}

type ParamsType = Partial<Omit<RequestType, 'categories'>> & {
  updateComment: string | null
  categories: number[]
}

export const DrawerContentRequestInfo: React.FC<
  DrawerContentRequestInfoProps
> = ({ request, onClose }) => {
  const invalidate = useInvalidateQueries()
  const {
    updateRequest: handleUpdateRequest,
    getComments,
    updateHistory,
  } = singleRequestStore

  // проверяет нужен  ли комментарий
  const inReportEditStatus =
    isAdminLabelAA() &&
    [15, 16].includes(request.status) &&
    dataEditReportStatuses.includes(request.reportStatus ?? -1)

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = useForm<ChangeInfoFormType>({
    defaultValues: getDefaultInfoValues(request),
    mode: 'onChange',
    resolver: zodResolver(changeInfoSchema(inReportEditStatus)),
  })

  const {
    onTerritoryChange,
    onDistrictChange,
    fullDotationRegions,
    categoriesItems,
    onCategoriesChange,
    districtOptions,
    localitiesOptions,
    locality,
    territory,
  } = useTerritoryFields(watch, setValue, request.reasonId)

  const onSave = async (data: ChangeInfoFormType) => {
    openModalFn?.['progress-backdrop']?.()
    const params: ParamsType = {
      ...data,
      phone: data.phone ? normalizePhoneNumber(data.phone) : data.phone,
      categories: formatUpdatedCategories(data.categories),
    }
    if (data?.updateComment?.length) params.updateComment = data.updateComment

    await updateRequest(request.id, params)
      .then((res: RequestType) => {
        handleUpdateRequest(res)
        invalidate(['requests-table'])
        getComments(request.id)
        updateHistory()
        closeModalFn?.['progress-backdrop']?.()
        notifySuccess('Заявка обновлена')
        onClose()
      })
      .finally(() => {
        closeModalFn?.['progress-backdrop']?.()
      })
  }

  const disableSaveBtn =
    !!Object.keys(errors).length ||
    (inReportEditStatus && !watch('updateComment')) ||
    !isAtLeastOneChanged(getDefaultInfoValues(request), watch)

  return (
    <Stack
      height={'100%'}
      justifyContent={'space-between'}
      component={'form'}
      onSubmit={handleSubmit(onSave)}
    >
      <ChangeInfoForm
        control={control}
        districtOptions={districtOptions}
        fullDotationRegions={fullDotationRegions}
        inReportEditStatus={inReportEditStatus}
        localitiesOptions={localitiesOptions}
        withDotationImage={fullDotationRegions?.includes(locality ?? '')}
        territory={territory}
        onCategoriesChange={onCategoriesChange}
        categoriesItems={categoriesItems}
        onTerritoryChange={onTerritoryChange}
        onDistrictChange={onDistrictChange}
      />
      <Box px={2}>
        <Button
          color={'primary'}
          variant={'contained'}
          sx={{ margin: '24px 0 0 0' }}
          fullWidth
          disabled={disableSaveBtn}
          type='submit'
        >
          <FormattedMessage id={'сохранить'} />
        </Button>
        <Button
          color={'primary'}
          variant={'outlined'}
          onClick={onClose}
          fullWidth
          disabled={disableSaveBtn}
          sx={{ margin: '8px 0 24px 0' }}
        >
          <FormattedMessage id={'Отмена'} />
        </Button>
      </Box>
    </Stack>
  )
}

function normalizePhoneNumber(phoneNumber: string) {
  return phoneNumber.replace(/\D/g, '')
}

export const formatUpdatedCategories = (categories: boolean[]) =>
  categories.map((c, i) => (!!c ? i : -1)).filter((i) => i >= 0)

const isAtLeastOneChanged = (
  defaultValues: ChangeInfoFormType,
  watch: UseFormWatch<ChangeInfoFormType>,
) => {
  let result = false
  const keys = Object.keys(defaultValues) as Array<keyof ChangeInfoFormType>

  keys.forEach((key) => {
    const currentItem = defaultValues[key]

    if (Array.isArray(currentItem)) {
      currentItem.forEach((item, index) => {
        if (item !== watch(key)?.[index]) result = true
      })
    } else if (typeof currentItem === 'object' && !!currentItem) {
      //  @ts-ignore
      if (currentItem?.key !== watch(key)?.key) result = true
    } else {
      if (currentItem !== watch(key) && key !== 'updateComment') result = true
    }
  })

  return result
}

export default observer(DrawerContentRequestInfo)
